"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

type RevealDirection = "up" | "left" | "right";

const hiddenTransforms: Record<RevealDirection, string> = {
  up: "translate3d(0, 28px, 0)",
  left: "translate3d(-40px, 0, 0)",
  right: "translate3d(40px, 0, 0)",
};

export default function ScrollReveal({
  children,
  direction = "up",
  className = "",
  delay = 0,
}: {
  children: ReactNode;
  direction?: RevealDirection;
  className?: string;
  delay?: number;
}) {
  const elementRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setVisible(true);
        observer.unobserve(entry.target);
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={elementRef}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translate3d(0, 0, 0)" : hiddenTransforms[direction],
        filter: visible ? "blur(0)" : "blur(3px)",
        transition: `opacity 680ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 680ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, filter 680ms ease ${delay}ms`,
        willChange: visible ? "auto" : "opacity, transform, filter",
      }}
    >
      {children}
    </div>
  );
}
